import React, { useEffect } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import AppSettings from "../../storage/appSettings";
import { getApiKey } from "../../utils/api";
import Loading from "../Loading/Loading";

/**
 * MapScriptLoader
 *
 * Loads the Google Maps JS API using useJsApiLoader with the provided key.
 * Rendered only once a key is available so the loader never initializes with
 * an empty key (useJsApiLoader loads once on mount and caches the result).
 */
const MapScriptLoader = ({ googleMapsApiKey, children }) => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey,
  });

  if (!isLoaded) {
    return <Loading />;
  }

  return children;
};

/**
 * GoogleMapsLoader (Web)
 *
 * Wraps individual web map views and ensures the Google Maps JS API is loaded
 * via useJsApiLoader before rendering its children.
 *
 * - Uses the Google Maps API key stored in AppSettings.
 * - Sets the key from getApiKey if it has not been populated yet.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - The map content to render once loaded.
 */
const GoogleMapsLoader = ({ children }) => {
  const googleApiKey = AppSettings((state) => state.googleApiKey);
  const setGoogleApiKey = AppSettings((state) => state.setGoogleApiKey);

  // Set the API key if it is not already populated in the store.
  useEffect(() => {
    if (googleApiKey) return;

    let mounted = true;
    (async () => {
      try {
        const apiKeyResponse = await getApiKey("googleMaps");
        if (mounted && apiKeyResponse?.data) {
          setGoogleApiKey(apiKeyResponse.data);
        }
      } catch (error) {
        console.error("❌ Error fetching API key:", error);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [googleApiKey, setGoogleApiKey]);

  // Wait until the key is available before initializing the loader.
  if (!googleApiKey) {
    return <Loading />;
  }

  return <MapScriptLoader googleMapsApiKey={googleApiKey}>{children}</MapScriptLoader>;
};

export default GoogleMapsLoader;
